import React, { useState } from 'react';
import {
  Scale, FileText, Shield, CheckCircle, AlertTriangle,
  Download, Edit, Eye, Plus, Calendar, Building2,
  Clock, Upload
} from 'lucide-react';
import { Button } from '../ui/button';

const legalDocuments = [
  {
    id: 1,
    name: 'Terms of Service',
    version: '3.2',
    lastUpdated: 'Aug 1, 2025',
    status: 'active',
    type: 'terms'
  },
  {
    id: 2,
    name: 'Privacy Policy',
    version: '2.8',
    lastUpdated: 'Jul 15, 2025',
    status: 'active',
    type: 'privacy'
  },
  {
    id: 3,
    name: 'HIPAA Business Associate Agreement',
    version: '1.5',
    lastUpdated: 'Jun 20, 2025',
    status: 'active',
    type: 'hipaa'
  },
  {
    id: 4,
    name: 'Data Processing Agreement',
    version: '2.1',
    lastUpdated: 'May 10, 2025',
    status: 'active',
    type: 'dpa'
  },
  {
    id: 5,
    name: 'AI/ML Disclosure & Disclaimer',
    version: '1.3',
    lastUpdated: 'Aug 5, 2025',
    status: 'active',
    type: 'ai-disclaimer'
  },
  {
    id: 6,
    name: 'Clinical Content Disclaimer',
    version: '2.0',
    lastUpdated: 'Jul 28, 2025',
    status: 'active',
    type: 'clinical'
  },
];

const baaStatus = [
  { facility: 'Metro Health System', signed: true, date: 'Mar 15, 2024', expires: 'Mar 15, 2026' },
  { facility: 'Community Hospital Network', signed: true, date: 'Jan 10, 2024', expires: 'Jan 10, 2026' },
  { facility: 'Valley Health Partners', signed: true, date: 'Feb 28, 2024', expires: 'Feb 28, 2026' },
  { facility: 'Regional Medical Center', signed: false, date: null, expires: null },
  { facility: 'Sunrise Long-Term Care', signed: true, date: 'Jun 22, 2024', expires: 'Jun 22, 2026' },
];

const complianceChecks = [
  { name: 'HIPAA Compliance', status: 'compliant', lastAudit: 'Jul 2025' },
  { name: 'SOC 2 Type II', status: 'compliant', lastAudit: 'Jun 2025' },
  { name: 'ISO 27001', status: 'compliant', lastAudit: 'May 2025' },
  { name: 'GDPR (if applicable)', status: 'compliant', lastAudit: 'Jul 2025' },
];

const LegalPage = () => {
  const [activeTab, setActiveTab] = useState('documents');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Legal & Compliance</h1>
          <p className="text-gray-500 mt-1">Manage legal documents, BAAs, and compliance requirements</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export All
          </Button>
          <Button className="btn-primary gap-2">
            <Plus className="w-4 h-4" />
            New Document
          </Button>
        </div>
      </div>

      {/* Compliance Status */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
            <Shield className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Compliance Status: All Clear</h2>
            <p className="text-sm text-gray-600">All certifications are current and up to date</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {complianceChecks.map((check) => (
            <div key={check.name} className="bg-white rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs font-medium text-green-600">Compliant</span>
              </div>
              <p className="font-medium text-gray-900">{check.name}</p>
              <p className="text-xs text-gray-500 mt-1">Last audit: {check.lastAudit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {['documents', 'baa-tracking', 'disclaimers'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-[var(--niko-teal)] text-[var(--niko-teal)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </button>
          ))}
        </nav>
      </div>

      {/* Legal Documents */}
      {activeTab === 'documents' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {legalDocuments.map((doc) => (
            <div key={doc.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-[var(--niko-teal)]/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[var(--niko-teal)]" />
                </div>
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                  v{doc.version}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{doc.name}</h3>
              <p className="text-sm text-gray-500 mb-4">Updated {doc.lastUpdated}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="flex-1 gap-1">
                  <Eye className="w-3 h-3" />
                  View
                </Button>
                <Button variant="outline" size="sm" className="flex-1 gap-1">
                  <Edit className="w-3 h-3" />
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BAA Tracking */}
      {activeTab === 'baa-tracking' && (
        <div className="bg-white rounded-2xl border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Business Associate Agreement Status</h2>
            <p className="text-sm text-gray-500">Track BAA signing status for all facilities</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Facility</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Signed Date</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Expires</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {baaStatus.map((baa) => (
                  <tr key={baa.facility} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{baa.facility}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {baa.signed ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <CheckCircle className="w-3 h-3" />
                          Signed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                          <AlertTriangle className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{baa.date || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{baa.expires || '—'}</td>
                    <td className="px-6 py-4">
                      {baa.signed ? (
                        <Button variant="ghost" size="sm" className="gap-1">
                          <Download className="w-3 h-3" />
                          Download
                        </Button>
                      ) : (
                        <Button size="sm" className="gap-1 bg-[var(--niko-teal)] hover:bg-[var(--niko-teal-dark)]">
                          <Upload className="w-3 h-3" />
                          Request
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Disclaimers */}
      {activeTab === 'disclaimers' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">AI/Clinical Intelligence Disclaimers</h3>
            <div className="prose prose-sm max-w-none text-gray-600">
              <p className="mb-4">
                <strong>Ask Niko Clinical Intelligence Engine Disclaimer:</strong>
              </p>
              <p className="mb-4">
                Ask Niko is designed to provide clinical decision support based on evidence-based guidelines, 
                national standards, and best practices. However, Ask Niko is not a substitute for professional 
                medical judgment, clinical expertise, or facility-specific policies and procedures.
              </p>
              <p className="mb-4">
                Healthcare professionals must:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Verify all information against their facility's current policies</li>
                <li>Use clinical judgment appropriate to each patient situation</li>
                <li>Consult with appropriate specialists when indicated</li>
                <li>Report any inaccuracies or concerns to their supervisor</li>
              </ul>
              <p>
                NikoSafe Connect and Ask Niko do not provide medical diagnoses, treatment recommendations 
                for specific patients, or replace the need for qualified healthcare professionals.
              </p>
            </div>
            <div className="mt-6 flex gap-3">
              <Button variant="outline" className="gap-2">
                <Edit className="w-4 h-4" />
                Edit Disclaimer
              </Button>
              <Button variant="outline" className="gap-2">
                <Eye className="w-4 h-4" />
                Preview
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalPage;
